/**
 * Client-side auth state management
 * Uses localStorage for token persistence
 */

export interface User {
  id: string
  email: string
  name: string | null
}

export interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
}

const STORAGE_KEY = 'proof_pulse_auth'

/**
 * Get the current auth state from localStorage
 */
export function getAuthState(): AuthState {
  if (typeof window === 'undefined') {
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
    }
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return {
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
      }
    }

    const data = JSON.parse(stored) as Partial<AuthState>
    return {
      user: data.user ?? null,
      accessToken: data.accessToken ?? null,
      refreshToken: data.refreshToken ?? null,
      isAuthenticated: Boolean(data.accessToken && data.user),
    }
  } catch {
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
    }
  }
}

/**
 * Save auth state to localStorage
 */
export function setAuthState(user: User, accessToken: string, refreshToken: string): void {
  if (typeof window === 'undefined') return

  const state: AuthState = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated: true,
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

/**
 * Clear auth state (logout)
 */
export function clearAuthState(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Get the access token for API requests
 */
export function getAccessToken(): string | null {
  return getAuthState().accessToken
}

/**
 * Get the refresh token
 */
export function getRefreshToken(): string | null {
  return getAuthState().refreshToken
}

/**
 * Check if user is authenticated (for route guards)
 */
export function isAuthenticated(): boolean {
  return getAuthState().isAuthenticated
}
